// Permisos del módulo de Obras. Los configura el administrador de Grupo Atlante en
// Panel de Administrador → Gestión de permisos obras y los hace cumplir el API
// (book-bsk-api/BookBSK/Services/ObrasPermisos.cs). Aquí solo se adapta la pantalla con lo
// que devuelve GET /api/obras/permisos/mio para el usuario de la sesión.
import { useEffect, useState } from "react";
import { getApiInstance, initializeApi } from "./api";
import { CRITICAL_HITOS } from "../pages/dashboard/obras/hitosModel";

const permiso = (datos) => ({
    configuracion: "", editaTodo: false, campos: [], puedeEditar: false, puedeCrear: false,
    puedeArchivar: false, puedeCatalogos: false, gestionaPermisos: false, filtrado: false, ...datos,
});

// Permisos de ejemplo para /obras-mock (desarrollo, sin API): ?rol=total|imagen|consulta|estudio.
export const PERMISOS_DEMO = {
    total: permiso({ configuracion: "Total", editaTodo: true, puedeEditar: true, puedeCrear: true, puedeArchivar: true, puedeCatalogos: true }),
    imagen: permiso({ configuracion: "Imagen: solo PMI", campos: ["codPmi"], puedeEditar: true }),
    consulta: permiso({ configuracion: "Consulta" }),
    estudio: permiso({ configuracion: "Estudio: sus obras", filtrado: true }),
};

// Una petición por sesión: el permiso se guarda con el token con el que se pidió, y otro
// token (otro usuario, o el mismo tras volver a entrar) lo vuelve a pedir.
let cache = { token: null, promesa: null };

// Permiso del usuario, o null si no tiene acceso a Obras (el API responde 403).
export function fetchObrasPermiso() {
    const token = localStorage.getItem("token");
    if (cache.promesa && cache.token === token) return cache.promesa;
    const promesa = (async () => {
        await initializeApi();
        try {
            const response = await getApiInstance().get("/api/obras/permisos/mio");
            return permiso(response?.data || {});
        } catch (error) {
            if (error?.response?.status === 403) return null;
            throw error;
        }
    })();
    cache = { token, promesa };
    // Un fallo de red no se queda guardado: el siguiente intento vuelve a preguntar.
    promesa.catch(() => { if (cache.promesa === promesa) cache = { token: null, promesa: null }; });
    return promesa;
}

// { permiso, cargando, error } del usuario de la sesión.
export function useObrasPermiso() {
    const [estado, setEstado] = useState({ permiso: null, cargando: true, error: null });
    useEffect(() => {
        let vivo = true;
        fetchObrasPermiso()
            .then((resultado) => { if (vivo) setEstado({ permiso: resultado, cargando: false, error: null }); })
            .catch((error) => { if (vivo) setEstado({ permiso: null, cargando: false, error }); });
        return () => { vivo = false; };
    }, []);
    return estado;
}

// Solo los hitos guardan aparte estado, fecha real y notas (liquidacionNotas es un campo propio).
// Son los de la ficha (hitosModel.js), los mismos que ObrasPermisos.Hitos en el API.
const HITOS = new Set(CRITICAL_HITOS.map((hito) => hito.field));
const SUFIJOS_HITO = ["Estado", "Real", "Notas"];
// Campos que se guardan juntos: permitir uno de los que se dan (lleva) es permitir el bloque (cubre).
// País y ubicación van con sus códigos; CAPEX = coste m² × sup. construida, y la ficha deduce uno.
const BLOQUES = [
    { lleva: ["pais", "ciudad"], cubre: new Set(["pais", "ciudad", "codPais", "codUbicacion"]) },
    { lleva: ["capex", "cM2", "supCons"], cubre: new Set(["capex", "cM2", "supCons"]) },
];

// ¿Puede cambiar este campo de la ficha? (clave con la que se guarda: tienda, codPmi, zonif…).
// Misma regla que el API: un hito cubre su estado, fecha real y notas; país y ubicación van
// juntos, y también CAPEX, coste m² y sup. construida.
export function canEditObraField(permisoObras, field) {
    if (!permisoObras || !field) return false;
    if (permisoObras.editaTodo) return true;
    const campos = new Set(permisoObras.campos || []);
    if (campos.has(field)) return true;
    if (BLOQUES.some((bloque) => bloque.cubre.has(field) && bloque.lleva.some((campo) => campos.has(campo)))) return true;
    return SUFIJOS_HITO.some((sufijo) => {
        const base = field.slice(0, -sufijo.length);
        return field.length > sufijo.length && field.endsWith(sufijo) && HITOS.has(base) && campos.has(base);
    });
}

// Lo mismo para un campo de una obra concreta: una obra nueva o duplicada (_isNew) se rellena
// entera, porque el alta envía la ficha completa y el API solo pide poder crear.
export const canEditObraFieldOf = (permisoObras, obra, field) =>
    (obra?._isNew ? Boolean(permisoObras?.puedeCrear) : canEditObraField(permisoObras, field));

// Texto corto de lo que puede hacer, para la barra de la hoja.
export function obrasAccessSummary(permisoObras) {
    if (!permisoObras?.puedeEditar) return permisoObras?.filtrado ? "Solo consulta · obras filtradas" : "Solo consulta";
    if (!permisoObras.editaTodo) return "Edición limitada";
    return "Sin altas de obra";
}
